<template>
    <div class="date-picker-container">
        <div class="date-picker-header">
            <button class="nav-button" :aria-label="navLabels.prev" @click="handleNavigation('prev')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="chevron-left"
                    aria-hidden="true"
                >
                    <path d="M15 18l-6-6 6-6" />
                </svg>
            </button>
            <div class="month-year-selector">
                <button
                    ref="monthSelector"
                    type="button"
                    class="clickable"
                    :aria-label="`Choose month, currently ${currentMonthName}`"
                    @click="setView('months')"
                >
                    {{ currentMonthName }}
                </button>
                <button
                    type="button"
                    class="clickable"
                    :aria-label="`Choose year, currently ${currentYear}`"
                    @click="setView('years')"
                >
                    {{ currentYear }}
                </button>
            </div>
            <button class="nav-button" :aria-label="navLabels.next" @click="handleNavigation('next')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="chevron-right"
                    aria-hidden="true"
                >
                    <path d="M9 18l6-6-6-6" />
                </svg>
            </button>
        </div>

        <div class="date-display-row" v-if="currentView === 'days'">
            <div class="selected-date">
                {{ formatSelectedDate }}
            </div>
        </div>

        <!-- Without this the whole recent stretch of the calendar is greyed with no reason given. -->
        <p class="age-hint" v-if="minAge > 0 && currentView === 'days'">
            Must be {{ minAge }} or older, so dates after {{ cutoffLabel }} cannot be picked.
        </p>

        <!-- Days View -->
        <div class="calendar" v-if="currentView === 'days'">
            <div class="weekdays">
                <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
            </div>
            <div ref="daysGrid" class="days" role="grid" :aria-label="`${currentMonthName} ${currentYear}`">
                <div v-for="(week, weekIndex) in calendarWeeks" :key="weekIndex" role="row" class="grid-row">
                    <!-- aria-selected belongs on the gridcell: it is not a state the button role
                         carries. aria-current="date" stays for today, which is what it means. -->
                    <div
                        v-for="day in week"
                        :key="day.date.getTime()"
                        role="gridcell"
                        class="grid-cell"
                        :aria-selected="isSelected(day.date) ? 'true' : 'false'"
                    >
                        <button
                            type="button"
                            :class="[
                                'day',
                                { 'current-month': day.currentMonth },
                                { 'other-month': !day.currentMonth },
                                { selected: isSelected(day.date) },
                                { disabled: day.disabled },
                            ]"
                            :disabled="day.disabled"
                            :tabindex="rovingDay && sameDay(day.date, rovingDay) ? 0 : -1"
                            :aria-label="dayLabelFormat.format(day.date)"
                            :aria-current="sameDay(day.date, today) ? 'date' : undefined"
                            @click="selectDate(day.date)"
                            @keydown="onDayKeydown($event, day.date)"
                        >
                            {{ day.day }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Months View -->
        <div
            ref="monthsGrid"
            class="months-grid"
            v-if="currentView === 'months'"
            role="grid"
            :aria-label="`Choose month, ${currentYear}`"
        >
            <div v-for="(row, rowIndex) in monthRows" :key="rowIndex" role="row" class="grid-row">
                <div v-for="month in row" :key="month.name" role="gridcell" class="grid-cell">
                    <button
                        type="button"
                        :class="['month-item', { selected: currentMonth === month.index }]"
                        :tabindex="rovingMonth === month.index ? 0 : -1"
                        :aria-label="month.name"
                        :aria-current="currentMonth === month.index ? 'true' : undefined"
                        @click="selectMonth(month.index)"
                        @keydown="onMonthKeydown($event, month.index)"
                    >
                        {{ month.name.substring(0, 3) }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Years View -->
        <div ref="yearsGrid" class="years-grid" v-if="currentView === 'years'" role="grid" aria-label="Choose year">
            <div v-for="(row, rowIndex) in yearRows" :key="rowIndex" role="row" class="grid-row">
                <div v-for="year in row" :key="year" role="gridcell" class="grid-cell">
                    <button
                        type="button"
                        :class="['year-item', { selected: currentYear === year }]"
                        :tabindex="rovingYear === year ? 0 : -1"
                        :aria-current="currentYear === year ? 'true' : undefined"
                        @click="selectYear(year)"
                        @keydown="onYearKeydown($event, year)"
                    >
                        {{ year }}
                    </button>
                </div>
            </div>
        </div>

        <div class="actions" v-if="currentView === 'days'">
            <PrimeButton label="Cancel" @click="cancel" variant="secondary" class="p-btn-root_secondary w-full" />
            <PrimeButton label="Apply" @click="apply" variant="primary" class="w-full" :loading="isApplying" />
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { computed, nextTick, onMounted, ref } from 'vue';

// Props and emits
const props = defineProps({
    initialDate: {
        type: Date,
        default: () => new Date(),
    },
    isApplying: {
        type: Boolean,
        default: false,
    },
    minAge: {           
        type: Number,
        default: 0,   
    },
});

const emit = defineEmits(['cancel', 'apply']);

// State
const currentDate = ref(new Date(props.initialDate || new Date()));
const selectedDate = ref(new Date(props.initialDate || new Date()));
const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const currentView = ref('days'); // 'days', 'months', 'years'
// Fixed at setup: a picker open across midnight marking two different cells as today would be
// stranger than one marking the day it opened on.
const today = new Date();
const yearRangeStart = ref(2020);

// Roving tabindex: one cell per grid is tabbable, the arrow keys move it.
const daysGrid = ref<HTMLElement | null>(null);
const monthSelector = ref<HTMLButtonElement | null>(null);
const monthsGrid = ref<HTMLElement | null>(null);
const yearsGrid = ref<HTMLElement | null>(null);
const focusedDay = ref<Date | null>(null);
const focusedMonth = ref<number | null>(null);
const focusedYear = ref<number | null>(null);

// Months and years render three per row — see .months-grid / .years-grid.
const GRID_COLUMNS = 3;

// Computed properties
const currentYear = computed(() => currentDate.value.getFullYear());
const currentMonth = computed(() => currentDate.value.getMonth());
const currentMonthName = computed(() => {
    return monthNames[currentMonth.value];
});

const maxSelectableDate = computed(() => {
    const today = new Date();
    return new Date(today.getFullYear() - props.minAge, today.getMonth(), today.getDate());
})

// The visible cell text is only the day number, so each cell carries the full date as its name.
const dayLabelFormat = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
});

const navLabels = computed(() => {
    if (currentView.value === 'years') {
        return { prev: 'Previous years', next: 'Next years' };
    }
    if (currentView.value === 'months') {
        return { prev: 'Previous year', next: 'Next year' };
    }
    return { prev: 'Previous month', next: 'Next month' };
});

const cutoffLabel = computed(() => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(maxSelectableDate.value);
});

const formatSelectedDate = computed(() => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(selectedDate.value);
});

const yearRange = computed(() => {
    const years = [];
    for (let i = 0; i < 12; i++) {
        years.push(yearRangeStart.value + i);
    }
    return years;
});

const calendarDays = computed(() => {
    const year = currentYear.value;
    const month = currentMonth.value;

    // First day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDay = new Date(year, month, 1).getDay();
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0).getDate();
    // Last day of previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    // Adjust for Monday as first day of week
    const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;

    const days = [];

    // Previous month days
    for (let i = firstDayAdjusted - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthLastDay - i);
        days.push({
            day: prevMonthLastDay - i,
            currentMonth: false,
            date,
            disabled: date > maxSelectableDate.value,
        });
    }

    // Current month days
    for (let i = 1; i <= lastDay; i++) {
        const date = new Date(year, month, i);
        days.push({
            day: i,
            currentMonth: true,
            date,
            disabled: date > maxSelectableDate.value,
        });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        days.push({
            day: i,
            currentMonth: false,
            date,
            disabled: date > maxSelectableDate.value,
        });
    }

    return days;
});

const calendarWeeks = computed(() => chunk(calendarDays.value, 7));

const monthRows = computed(() => chunk(monthNames.map((name, index) => ({ name, index })), GRID_COLUMNS));

const yearRows = computed(() => chunk(yearRange.value, GRID_COLUMNS));

const rovingDay = computed(() => {
    const days = calendarDays.value;
    const isTabbable = (candidate: Date) => days.some((day) => !day.disabled && sameDay(day.date, candidate));

    if (focusedDay.value && isTabbable(focusedDay.value)) return focusedDay.value;
    if (isTabbable(selectedDate.value)) return selectedDate.value;
    if (isTabbable(new Date())) return new Date();

    // A month past the age cutoff has no selectable day at all. Handing tabindex="0" to a
    // disabled one leaves the grid with no reachable cell and nothing for focus to land on.
    return days.find((day) => !day.disabled)?.date ?? null;
});

const rovingMonth = computed(() => focusedMonth.value ?? currentMonth.value);

const rovingYear = computed(() => {
    const years = yearRange.value;
    if (focusedYear.value !== null && years.includes(focusedYear.value)) return focusedYear.value;
    return years.includes(currentYear.value) ? currentYear.value : years[0];
});

// Methods
function chunk<T>(items: T[], size: number): T[][] {
    const rows: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size));
    }
    return rows;
}

function sameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Monday-first, matching the weekday header row.
function weekdayIndex(date: Date) {
    return (date.getDay() + 6) % 7;
}

function addMonths(date: Date, delta: number) {
    const target = new Date(date.getFullYear(), date.getMonth() + delta, 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(date.getDate(), lastDay));
    return target;
}

function focusRovingCell(grid: HTMLElement | null) {
    const roving = grid?.querySelector<HTMLButtonElement>('button[tabindex="0"]');
    if (roving && !roving.disabled) {
        roving.focus();
        return;
    }

    // Nothing in the grid can take focus — every day is past the cutoff. The month selector is
    // the control that gets the user back out, so focus lands there rather than on the document.
    monthSelector.value?.focus();
}

function dayFocusTarget(key: string, from: Date) {
    const year = from.getFullYear();
    const month = from.getMonth();
    const date = from.getDate();

    switch (key) {
        case 'ArrowLeft':
            return new Date(year, month, date - 1);
        case 'ArrowRight':
            return new Date(year, month, date + 1);
        case 'ArrowUp':
            return new Date(year, month, date - 7);
        case 'ArrowDown':
            return new Date(year, month, date + 7);
        case 'Home':
            return new Date(year, month, date - weekdayIndex(from));
        case 'End':
            return new Date(year, month, date + (6 - weekdayIndex(from)));
        case 'PageUp':
            return addMonths(from, -1);
        case 'PageDown':
            return addMonths(from, 1);
        default:
            return null;
    }
}

async function onDayKeydown(event: KeyboardEvent, date: Date) {
    // Enter is left to the button: it fires a click, which selects the day and nothing more —
    // the same outcome as Space and as a mouse. Committing here instead would save a birthday
    // out from under someone who pressed Enter to choose one.
    const target = dayFocusTarget(event.key, date);
    if (!target) return;

    event.preventDefault();
    if (target > maxSelectableDate.value) return;

    focusedDay.value = target;
    if (target.getMonth() !== currentMonth.value || target.getFullYear() !== currentYear.value) {
        currentDate.value = new Date(target.getFullYear(), target.getMonth(), 1);
    }

    await nextTick();
    focusRovingCell(daysGrid.value);
}

function gridFocusTarget(key: string, index: number, lastIndex: number) {
    const deltas: Record<string, number> = {
        ArrowLeft: -1,
        ArrowRight: 1,
        ArrowUp: -GRID_COLUMNS,
        ArrowDown: GRID_COLUMNS,
    };
    const delta = deltas[key];
    if (delta === undefined) return null;

    const target = index + delta;
    return target < 0 || target > lastIndex ? index : target;
}

async function onMonthKeydown(event: KeyboardEvent, index: number) {
    const target = gridFocusTarget(event.key, index, monthNames.length - 1);
    if (target === null) return;

    event.preventDefault();
    focusedMonth.value = target;
    await nextTick();
    focusRovingCell(monthsGrid.value);
}

async function onYearKeydown(event: KeyboardEvent, year: number) {
    const years = yearRange.value;
    const target = gridFocusTarget(event.key, years.indexOf(year), years.length - 1);
    if (target === null) return;

    event.preventDefault();
    focusedYear.value = years[target];
    await nextTick();
    focusRovingCell(yearsGrid.value);
}

function handleNavigation(direction: 'prev' | 'next') {
    if (currentView.value === 'days') {
        if (direction === 'prev') {
            previousMonth();
        } else {
            nextMonth();
        }
    } else if (currentView.value === 'months') {
        if (direction === 'prev') {
            currentDate.value = new Date(currentYear.value - 1, currentMonth.value, 1);
        } else {
            currentDate.value = new Date(currentYear.value + 1, currentMonth.value, 1);
        }
    } else if (currentView.value === 'years') {
        if (direction === 'prev') {
            yearRangeStart.value -= 12;
        } else {
            yearRangeStart.value += 12;
        }
    }
}

function previousMonth() {
    currentDate.value = new Date(currentYear.value, currentMonth.value - 1, 1);
}

function nextMonth() {
    currentDate.value = new Date(currentYear.value, currentMonth.value + 1, 1);
}

function selectDate(date: Date) {
    // Create a new date at noon to avoid timezone issues
    selectedDate.value = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
}

function isSelected(date: Date) {
    return sameDay(date, selectedDate.value);
}

function setView(view: 'days' | 'months' | 'years') {
    currentView.value = view;
    focusedMonth.value = null;
    focusedYear.value = null;
    if (view === 'years') {
        // Center the year range around the current year
        yearRangeStart.value = Math.floor(currentYear.value / 12) * 12;
    }
}

// Switching view unmounts the button that was just activated, so focus has to be handed to the
// grid replacing it or it falls back to the document and the keyboard user loses the calendar.
function selectMonth(monthIndex: number) {
    currentDate.value = new Date(currentYear.value, monthIndex, 1);
    currentView.value = 'days';
    void nextTick(() => focusRovingCell(daysGrid.value));
}

function selectYear(year: number) {
    currentDate.value = new Date(year, currentMonth.value, 1);
    currentView.value = 'months';
    void nextTick(() => focusRovingCell(monthsGrid.value));
}

function cancel() {
    emit('cancel');
}

function apply() {
    emit('apply', selectedDate.value);
}

// Initialize with January 2025 and select January 10th as shown in the design
onMounted(() => {
    if (props.initialDate) {
        currentDate.value = new Date(props.initialDate);
        selectedDate.value = new Date(props.initialDate);
    } else {
        currentDate.value = new Date(2025, 0, 1); // January 2025
        selectedDate.value = new Date(2025, 0, 10); // January 10, 2025
    }
});
</script>

<style scoped>
.date-picker-container {
    @apply sm:w-[360px] w-11/12 mx-auto bg-white rounded-lg shadow-lg p-4;
}

.date-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.age-hint {
    font-family: var(--font-sans);
    font-size: 12px;
    line-height: 16px;
    color: var(--ink-muted);
    margin-bottom: 8px;
}

.day.disabled {
    color: var(--ink-faint);
    pointer-events: none;
    cursor: not-allowed;
}

.month-year-selector {
    font-size: 20px;
    font-weight: 500;

    display: flex;
    gap: 8px;
    color: var(--ink-body);
    text-align: center;

    /* Text md/Semibold */
    font-family: var(--font-sans);
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 150% */
}

.clickable {
    cursor: pointer;
}

.clickable:hover {
    color: var(--action);
}

.nav-button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--ink-body);
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nav-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.date-display-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
}

.selected-date {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid var(--hairline-strong);
    border-radius: 6px;
    font-size: 16px;
    color: var(--ink-body);
    overflow: hidden;
    color: var(--ink);
    text-overflow: ellipsis;

    /* Text md/Regular */
    font-family: var(--font-sans);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 150% */
}

.calendar {
    margin-bottom: 20px;
}

.weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    margin-bottom: 8px;
}

.weekday {
    font-size: 14px;
    color: var(--ink-body);
    padding: 8px 0;
    color: var(--ink-body);
    text-align: center;

    /* Text sm/Medium */
    font-family: var(--font-sans);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
}

.days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

/* Rows and cells carry grid semantics only; the buttons stay the grid items. */
.grid-row,
.grid-cell {
    display: contents;
}

.day {
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 50%;
    cursor: pointer;
    color: var(--ink-body);
    text-align: center;

    /* Text sm/Regular */
    font-family: var(--font-sans);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
}

.current-month {
    color: var(--ink-body);
}

.other-month {
    color: var(--ink-faint);
}

.selected {
    background-color: var(--action);
    color: var(--ink-invert);
}

.day:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

/* Months View */
.months-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 16px 0;
}

.month-item {
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 8px;
    cursor: pointer;
}

.month-item:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

/* Years View */
.years-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 16px 0;
}

.year-item {
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 8px;
    cursor: pointer;
}

.year-item:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

.actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    border-top: 1px solid var(--hairline);
    padding-top: 20px;
    gap: 12px;
}

.cancel-button,
.apply-button {
    padding: 12px 0;
    width: 48%;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    text-align: center;
}

.cancel-button {
    background-color: var(--surface);
    border: 1px solid var(--hairline-strong);
    color: var(--ink-body);
}

.apply-button {
    background-color: var(--action);
    border: none;
    color: var(--ink-invert);
}

.cancel-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.apply-button:hover {
    background-color: var(--action-hover);
}
</style>
